import { CATEGORY } from '../components/ui/categoryColors';
import type { ChargeState, LocationId, ChargerView, Rollup } from './types';

// Status palette — mirrors the smrtchrgr-agg case colors. No emojis: state is
// conveyed by a colored dot + text label.
//   dot    — tailwind classes for the small status dot (bg)
//   text   — text color for counts/labels
//   anim   — optional breathing animation key (see ChargeControl.css)
// The class strings are literal because Tailwind only emits classes it can read in source. Each
// oklch() value is CATEGORY[hue].mark (dot) or .ink (text); STATE_HEX below reads CATEGORY directly.
export const STATE_UI: Record<ChargeState, { label: string; dot: string; text: string; anim?: 'charge' | 'discharge' }> = {
  charging:    { label: 'Charging',    dot: 'bg-[oklch(0.7_0.149_72)]',  text: 'text-[oklch(0.83_0.13_72)]',   anim: 'charge' },
  charged:     { label: 'Charged',     dot: 'bg-[oklch(0.7_0.141_168)]', text: 'text-[oklch(0.83_0.13_168)]' },
  discharging: { label: 'Discharging', dot: 'bg-[oklch(0.7_0.142_232)]', text: 'text-[oklch(0.83_0.103_232)]', anim: 'discharge' },
  finished:    { label: 'Finished',    dot: 'bg-[oklch(0.7_0.121_188)]', text: 'text-[oklch(0.83_0.13_188)]' },
  fault:       { label: 'Fault',       dot: 'bg-[oklch(0.7_0.16_25)]',   text: 'text-[oklch(0.83_0.094_25)]' },
  standby:     { label: 'Standby',     dot: 'bg-[oklch(0.7_0_0)]',       text: 'text-[oklch(0.83_0_0)]' },
  empty:       { label: 'Empty',       dot: 'bg-surface-container-highest', text: 'text-on-surface-variant' },
};

// Order used when rendering rollup chips (most actionable first).
export const ROLLUP_ORDER: ChargeState[] = [
  'charging', 'charged', 'discharging', 'finished', 'fault', 'standby', 'empty',
];

export function socColor(soc: number | null): string {
  if (soc == null) return 'text-on-surface-variant';
  if (soc >= 80) return 'text-[oklch(0.83_0.13_168)]';
  if (soc >= 40) return 'text-[oklch(0.83_0.13_72)]';
  return 'text-[oklch(0.83_0.094_25)]';
}

// Hex equivalents of the status palette, for the conic SOC ring's fill color
// (CSS custom property — Tailwind classes don't reach into the gradient).
export const STATE_HEX: Record<ChargeState, string> = {
  charging:    CATEGORY.amber.mark.hex,
  charged:     CATEGORY.emerald.mark.hex,
  discharging: CATEGORY.sky.mark.hex,
  finished:    CATEGORY.teal.mark.hex,
  fault:       CATEGORY.red.mark.hex,
  standby:     CATEGORY.neutral.mark.hex,
  empty:       '#3A3A3A',
};

// Dominant battery state of a charger — drives the ring color + glow and the
// card's ambient animation. Most-actionable first; null when no batteries.
export function dominantState(charger: ChargerView): ChargeState | null {
  const c = charger.rollup?.counts;
  if (!c) return null;
  if ((c.charging || 0) > 0) return 'charging';
  if ((c.discharging || 0) > 0) return 'discharging';
  if ((c.fault || 0) > 0) return 'fault';
  if ((c.charged || 0) > 0) return 'charged';
  if ((c.finished || 0) > 0) return 'finished';
  if ((c.standby || 0) > 0) return 'standby';
  return null;
}

// Ring fill color for a charger: idle gray when no batteries present, else the
// dominant state's color (faithful to the mockup — green when charged, amber
// while charging, etc.). The fill *amount* already conveys SOC level.
export function ringColor(charger: ChargerView): string {
  if (!(charger.rollup?.present_count)) return STATE_HEX.empty;
  return STATE_HEX[dominantState(charger) ?? 'charged'];
}

// SOC-band hex (mirrors socColor) — used for the aggregate dashboard/location
// ring, which has no single dominant state.
export function socHex(soc: number | null): string {
  if (soc == null) return STATE_HEX.empty;
  if (soc >= 80) return CATEGORY.emerald.mark.hex;
  if (soc >= 40) return CATEGORY.amber.mark.hex;
  return CATEGORY.red.mark.hex;
}

// ---- location id <-> prefs sentinel ----------------------------------------

export function prefsToLocationId(current: string | undefined | null): LocationId {
  if (!current || current === 'mine') return 'mine';
  // A site whose declaration was later removed resolves back to 'mine' at the
  // picker, since the chip it would select no longer exists.
  if (current.startsWith('site:') && current.length > 'site:'.length) {
    return current as LocationId;
  }
  if (current.startsWith('loc:')) {
    const n = parseInt(current.slice(4), 10);
    if (Number.isInteger(n)) return n;
  }
  return 'mine';
}

export function locationIdToPrefs(id: LocationId): string {
  return typeof id === 'string' ? id : `loc:${id}`;
}

// Query value for /charge/fleet?location_id=
export function locationIdParam(id: LocationId): string {
  return typeof id === 'string' ? id : String(id);
}

// 'site:Headquarters' -> 'Headquarters'; anything else -> null.
export function siteLabel(id: LocationId): string | null {
  return typeof id === 'string' && id.startsWith('site:') ? id.slice('site:'.length) : null;
}

// Charger's own Flask UI (only reachable when the user's device is on the VPN).
export function vpnDeepLink(vpnIp: string): string {
  return `http://${vpnIp}:5000/home`;
}

// Aggregate every charger's rollup into one location-level summary.
// Shared by the Charge Control page and the dashboard widget.
export function aggregateFleet(fleet: ChargerView[]): { rollup: Rollup; online: number; total: number } {
  const counts = ROLLUP_ORDER.reduce((a, s) => ((a[s] = 0), a), {} as Record<ChargeState, number>);
  let socSum = 0, socN = 0, maxTemp: number | null = null, thermal = 0, present = 0, eta: number | null = null, online = 0;
  const goals = new Set<number>();
  for (const c of fleet) {
    if (c.online) online += 1;
    if (c.goal_pct != null) goals.add(c.goal_pct);
    const r = c.rollup;
    if (!r) continue;
    for (const s of ROLLUP_ORDER) counts[s] += r.counts?.[s] || 0;
    if (r.soc_avg != null && r.present_count) { socSum += r.soc_avg * r.present_count; socN += r.present_count; }
    if (r.max_temp != null) maxTemp = Math.max(maxTemp ?? -Infinity, r.max_temp);
    thermal += r.thermal_warnings || 0;
    present += r.present_count || 0;
    if (r.eta_hours_max != null) eta = Math.max(eta ?? 0, r.eta_hours_max);
  }
  return {
    online, total: fleet.length,
    rollup: {
      counts, present_count: present, soc_avg: socN ? socSum / socN : null,
      max_temp: maxTemp, thermal_warnings: thermal, eta_hours_max: eta,
      // Representative location goal: the shared value if all chargers agree, else
      // null (mixed) — so the hero slider reflects the real goal, not a default.
      goal_pct: goals.size === 1 ? [...goals][0] : null,
    },
  };
}

export function fmtEta(hours: number | null | undefined): string {
  if (hours == null || !isFinite(hours) || hours <= 0) return '—';
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return m ? `${h}h ${m}m` : `${h}h`;
}

// Relative "time ago" for a charger's last successful poll (collector-stamped
// ISO). Recomputed each render (the page polls every 3s). Most useful on an
// offline charger — an online one is effectively "just now".
export function fmtLastSeen(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!isFinite(t)) return null;
  const secs = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (secs < 45) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

// h:mm digital-clock form for the ETA metric tile (mirrors the charger app).
export function fmtEtaClock(hours: number | null | undefined): string {
  if (hours == null || !isFinite(hours) || hours <= 0) return '—';
  const total = Math.round(hours * 60);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}
